import { useTranslation } from 'react-i18next'

import {
  BorderRadius,
  TertiaryButton,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { iconSize } from '~/constants/iconSize'
import { autoBidPriceEffectiveText } from '~/pages/AdminApprovalFlow/components/AutoBidPriceRangeHint'
import { WarningTriangleIcon } from '~/pages/AdminApprovalFlow/components/icons'
import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import {
  type AutoBidEffective,
  type AutoBidSideLocal,
  autoBidSideSummaryText,
} from '~/pages/AdminApprovalFlow/models/autoBid'

/**
 * 買い行 / 売り行 1 行ぶんの入力。
 *
 * スポットは `side` (= [[AutoBidSideLocal]]) を渡し、書式は
 * [[autoBidSideSummaryText]] がこの component 内で組む。
 *
 * 時間前入札は項目が別物 (価格モード / 基準 / 対向参照価格) で
 * `AutoBidSideLocal` に収まらないため、**整形済みの `text` を渡す**
 * (書式は [[autoBidIntradaySideSummaryText]]、AC-APF-047)。
 * ここで `AutoBidSideLocal` を無理に広げると、spot 用の既定値を持った
 * 空オブジェクトを作る羽目になり、未保存を設定済みに見せる余地が生まれる。
 */
export type SideRow = {
  /** undefined = 価格の実効値を出せない (時間前の動的基準)。 ⚠ も付けない (AC-APF-047)。 */
  effective: AutoBidEffective | undefined
} & (
  | { side: AutoBidSideLocal; text?: undefined }
  | { side?: undefined; text: string }
)

type Props = {
  buy: SideRow
  sell: SideRow
  onEdit: () => void
  /**
   * true の時、 buy/sell のデフォルト値サマリを表示せず「未設定（ルール編集が
   * 必要）」を表示する (AC-01/AC-056b: 承認モード=自動 のエリアで auto_bid_rule
   * が未保存の間、システム既定値のまま自動発注される見た目を作らない
   * = eBalance 鉄則2)。
   */
  unsaved?: boolean
}

/**
 * 自動セルの 2 行サマリ (AC-01: 買い行 / 売り行、書式 `カバー率{N}% / {発注内容}`)。
 * bid-limits 実効値が clamp / 成行→指値変換された場合、 または JEPX の値域外の場合は
 * ⚠ を付ける (AC-APF-047)。 実効値の文言はスポットの clamp・成行変換だけに添え、
 * 時間前と JEPX 値域外は ⚠ のみ (spec・design とも要約に文言を定めていない)。
 * ルールデータ自体は書き換えない (= 表示専用)。
 * unsaved=true の間は buy/sell を描画せず未設定状態を表示する (AC-056b)。
 */
export const AutoBidCellSummary = ({
  buy,
  sell,
  onEdit,
  unsaved = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (unsaved) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          alignItems: 'flex-start',
          marginTop: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: fontSizes.sm.value,
            // 「自動入札ルール未設定」 は保存をブロックする要因なので、 他の
            // ブロック警告 (承認者未設定 等) と同じ濃い赤・700 に揃える
            // (design admin/approval-flow.html:365・:367 = --text-negative-high +
            // font-weight 700)。 以前は黄土色 (textWarning) で、 同じ保存ブロック
            // 要因なのに色が割れていた。
            color: eclColor.textNegativeHigh,
            fontWeight: 700,
          }}
        >
          <span aria-hidden="true" style={{ display: 'inline-flex' }}>
            <WarningTriangleIcon
              boxSize={iconSize.fieldMessage}
              color={eclColor.textNegativeHigh}
            />
          </span>
          <span>{t('page:adminApprovalFlow.autoBid.unsaved')}</span>
        </div>
        <TertiaryButton onClick={onEdit} size="sm">
          {t('page:adminApprovalFlow.autoBid.editButton')}
        </TertiaryButton>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        alignItems: 'flex-start',
        marginTop: '8px',
      }}
    >
      <AutoBidSideLine
        row={buy}
        label={t('page:adminApprovalFlow.autoBid.side.buy')}
        side="buy"
      />
      <AutoBidSideLine
        row={sell}
        label={t('page:adminApprovalFlow.autoBid.side.sell')}
        side="sell"
      />
      <TertiaryButton onClick={onEdit} size="sm">
        {t('page:adminApprovalFlow.autoBid.editButton')}
      </TertiaryButton>
    </div>
  )
}

/**
 * 買い行 / 売り行 1 行ぶん。 セル内サマリ (AC-APF-047) と一括 = 自動 の
 * 折りたたみ要約 (AC-APF-060) の **両方**がこれを通るので、 書式・⚠ の付き方が
 * 2 か所で割れない (design `admin/approval-flow.html` も同じ関数を共有している)。
 */
export const AutoBidSideLine = ({
  label,
  row,
  side,
}: {
  label: string
  row: SideRow
  side: 'buy' | 'sell'
}): React.ReactElement => {
  const { t } = useTranslation()
  // 整形済みの text が来ればそれを使う (時間前入札)。 無ければ spot の書式で組む。
  const text =
    row.text !== undefined ? row.text : autoBidSideSummaryText(t, row.side)
  const { effective } = row
  const warning = effective?.warning ?? false

  return (
    <div
      data-warning={warning || undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        // design `.apr-autobid-cellsum-line`: 食いつき条件など長い要約が入るので
        // 折返しを許す (nowrap のままだとセル幅で切れて読めない)。gap は
        // 「行間 0 / 語間 4px」で、折り返しても行が離れないようにする。
        flexWrap: 'wrap',
        gap: '0 4px',
        lineHeight: 1.45,
        fontSize: fontSizes.sm.value,
        // 数値 (カバー率・価格) が縦に並ぶので桁を揃える
        fontVariantNumeric: 'tabular-nums',
        color: eclColor.textHigh,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          minWidth: '1.7em',
          padding: '0 4px',
          borderRadius: BorderRadius.radiiBase.value,
          background: eclColor.surfaceSecondary,
          fontSize: fontSizes.xs.value,
          fontWeight: 700,
          lineHeight: 1.6,
          // design `.apr-autobid-cellsum-side--buy` / `--sell`。買い = teal /
          // 売り = red は市場系画面で共通の対応 (Text.bid / Text.offer)。
          color: side === 'buy' ? eclColor.textBid : eclColor.textOffer,
        }}
      >
        {label}
      </span>
      {warning && (
        <span
          aria-hidden="true"
          style={{
            color: eclColor.textWarning,
            display: 'inline-flex',
            flexShrink: 0,
          }}
        >
          <WarningTriangleIcon color={eclColor.textWarning} />
        </span>
      )}
      <span>{text}</span>
      {warning &&
        effective !== undefined &&
        row.side !== undefined &&
        !effective.outsideJepx && (
          <span style={{ color: eclColor.textWarning, fontWeight: 600 }}>
            {effective.marketConverted
              ? t('page:adminApprovalFlow.autoBid.marketNotAllowed', {
                  value: effective.effectivePrice?.toFixed(2) ?? '',
                })
              : autoBidPriceEffectiveText(t, effective)}
          </span>
        )}
    </div>
  )
}
